
import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Home } from 'lucide-react';
import { format } from 'date-fns';

type ProfileHeaderProps = {
  profile: {
    first_name: string;
    last_name: string;
    current_family_id: string | null;
    created_at?: string;
  };
  email?: string | null;
  isFamilyAdmin: boolean; 
  familyAdmin?: { first_name: string; last_name: string; id: string } | null;
  userId?: string;
};

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile,
  email,
  isFamilyAdmin,
  familyAdmin,
  userId
}) => {
  const getInitials = (firstName: string) => {
    return firstName?.charAt(0).toUpperCase() || '';
  };

  // Check if the current user is the family admin
  const isUserFamilyAdmin = isFamilyAdmin && familyAdmin?.id === userId;

  return (
    <div className="flex flex-col md:flex-row md:items-start gap-4 w-full">
      <div className="flex items-start gap-4 flex-1">
        <Avatar className="h-12 w-12">
          <AvatarFallback>
            {getInitials(profile.first_name)}
          </AvatarFallback>
        </Avatar>
        <div className="space-y-2 flex-1">
          <h3 className="text-lg font-medium">{profile.first_name}</h3>
          <p className="text-sm text-muted-foreground">{email}</p>
          {profile.created_at && (
            <p className="text-xs text-muted-foreground">
              Joined on {format(new Date(profile.created_at), 'MMMM d, yyyy')}
            </p>
          )}
          {/* Only render family admin info if the user is NOT the family admin and family connection exists */}
          {profile.current_family_id && !isUserFamilyAdmin && (
            <div className="text-sm mt-2 flex gap-1.5 items-center">
              <Home className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Family Admin: </span> 
              {familyAdmin ? (
                <span>
                  {familyAdmin.first_name}
                  {familyAdmin.id === userId && " (You)"}
                </span>
              ) : (
                <span className="italic text-muted-foreground">No admin set</span>
              )}
            </div>
          )}
        </div>
      </div>
      {isFamilyAdmin && (
        <div className="flex gap-2 md:mt-1">
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Badge className="h-3 w-3" />
            <span>Admin</span>
          </Badge>
        </div>
      )}
    </div>
  );
};
